import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import CallbackModal from '../components/CallbackModal';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { usePageMeta } from '../utils/usePageMeta';
import { buildLocalBusiness } from '../utils/schema';

const BusinessPage = () => {
  const { t, language, lp } = useLanguage();
  const { items } = useCart();
  const hasCartItems = items.length > 0;
  const [showCallback, setShowCallback] = useState(false);

  usePageMeta({
    title: t('businessPage.metaTitle'),
    description: t('businessPage.metaDescription'),
    path: '/business',
    lang: language,
  });

  const benefits = [
    { title: t('businessPage.benefit1Title'), desc: t('businessPage.benefit1Desc') },
    { title: t('businessPage.benefit2Title'), desc: t('businessPage.benefit2Desc') },
    { title: t('businessPage.benefit3Title'), desc: t('businessPage.benefit3Desc') },
    { title: t('businessPage.benefit4Title'), desc: t('businessPage.benefit4Desc') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: t('businessPage.title'),
          name: `SmarTOOL — ${t('businessPage.title')}`,
          description: t('businessPage.metaDescription'),
          provider: (({ '@context': _ctx, ...business }) => business)(buildLocalBusiness(language)),
          areaServed: language === 'ru' ? 'Харьков' : 'Харків',
          audience: {
            '@type': 'BusinessAudience',
            audienceType: 'Будівельні компанії, ремонтні бригади, підрядники',
          },
        }) }}
      />

      <Header title={t('header.business')} showBack />

      <main className={`px-4 py-6 max-w-[600px] mx-auto animate-fade-in ${hasCartItems ? 'pb-[100px]' : 'pb-10'}`}>
        <div className="text-center mb-6">
          <h1 className="text-[26px] font-bold text-black m-0 mb-2">{t('businessPage.title')}</h1>
          <p className="text-base text-dewalt font-semibold m-0">{t('businessPage.subtitle')}</p>
        </div>

        <p className="text-base text-gray-600 leading-[1.6] m-0 mb-8 text-center">{t('businessPage.intro')}</p>

        <section className="mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map(({ title, desc }) => (
              <div key={title} className="bg-gray-100 rounded-2xl p-5 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                <h2 className="text-[15px] font-semibold text-black m-0 mb-1.5">{title}</h2>
                <p className="text-[13px] text-gray-500 m-0 leading-snug">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-[#fffbf0] border-2 border-dewalt rounded-2xl p-6 text-center">
          <h2 className="text-lg font-semibold text-black m-0 mb-2">{t('businessPage.ctaTitle')}</h2>
          <p className="text-sm text-gray-600 m-0 mb-5">{t('businessPage.ctaSubtitle')}</p>
          <button
            onClick={() => setShowCallback(true)}
            className="w-full max-w-[280px] bg-black text-dewalt rounded-2xl px-7 py-4 border-none cursor-pointer shadow-lg transition-all duration-150 active:scale-[0.97] hover:shadow-xl font-extrabold tracking-wide"
          >
            {t('businessPage.ctaButton')}
          </button>
        </section>

        <div className="mt-6 text-center">
          <Link to={lp('/')} className="text-black/50 underline text-sm">
            {t('businessPage.backToCatalog')}
          </Link>
        </div>
      </main>

      <CallbackModal
        isOpen={showCallback}
        onClose={() => setShowCallback(false)}
        toolName={t('businessPage.title')}
      />
    </div>
  );
};

export default BusinessPage;
